import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin } from 'lucide-react';
import { COPY, ROUTES } from '@/lib/data';
import { publicApi } from '@/lib/api';
import { usePublicContent } from '@/lib/usePublicContent';
import { trackEvent } from '@/lib/analytics';
import { fadeUp, stagger } from '@/lib/motion';
import { Particles, Blobs } from '@/components/public/Particles';
import { ReservationButton } from '@/components/public/ReservationButton';

export const Hero = () => {
    const { content = {}, settings = {} } = usePublicContent();

    const [heroMedia, setHeroMedia] = useState([]);
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        publicApi
            .getMedia('hero')
            .then((data) => {
                const media = Array.isArray(data)
                    ? data
                          .filter((item) => item?.url)
                          .sort(
                              (a, b) =>
                                  Number(a.order || 0) -
                                  Number(b.order || 0)
                          )
                    : [];

                setHeroMedia(media);
            })
            .catch(() => setHeroMedia([]));
    }, []);

    const slides = useMemo(() => {
        const adminSlides = heroMedia
            .map((item) => item.url)
            .filter(Boolean);

        return adminSlides.length
            ? adminSlides
            : (COPY.fallbackHeroImages || []).filter(Boolean);
    }, [heroMedia]);

    useEffect(() => {
        if (slides.length <= 1) return undefined;

        const timer = window.setInterval(() => {
            setIdx((current) => (current + 1) % slides.length);
        }, 6000);

        return () => window.clearInterval(timer);
    }, [slides.length]);

    useEffect(() => {
        if (slides.length === 0 || idx >= slides.length) {
            setIdx(0);
        }
    }, [slides.length, idx]);

    const headline =
        content.hero_headline ||
        COPY.heroDefault.headline;

    const subheadline =
        content.hero_subheadline ||
        COPY.heroDefault.subheadline;

    const ctaPrimary =
        content.hero_cta_primary_label ||
        COPY.heroDefault.ctaPrimary;

    const ctaSecondary =
        content.hero_cta_secondary_label ||
        COPY.heroDefault.ctaSecondary;

    const locationLabel =
        settings.address ||
        'Pifo, Quito';

    const onSecondary = () => {
        trackEvent('cta_click', '/', {
            from: 'hero',
            cta: 'ver_atracciones',
        });
    };

    return (
        <section
            data-testid="home-hero"
            className="home-hero-shell relative overflow-hidden bg-gradient-to-br from-[hsl(var(--kche-pink))] via-[hsl(var(--kche-purple))] to-[hsl(var(--kche-blue))]"
        >
            <div className="absolute inset-0 z-0 pointer-events-none opacity-60">
                <Blobs />
                <Particles count={8} />
            </div>

            <div
                aria-hidden="true"
                className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_82%_18%,rgba(0,217,255,0.16),transparent_28%),linear-gradient(135deg,rgba(255,255,255,0.08),rgba(255,255,255,0))]"
            />

            {/* Zona completa de imagen */}
            <motion.div
                initial={{ opacity: 0, x: -18 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                    duration: 0.5,
                    ease: [0.22, 1, 0.36, 1],
                }}
                className="hero-visual"
            >
                {/* Imagen y arco se desplazan juntos */}
                <div className="hero-motion-layer">
                    <div className="hero-image-mask">
                        {slides.map((src, slideIndex) => (
                            <img
                                key={`${src}-${slideIndex}`}
                                src={src}
                                alt=""
                                aria-hidden="true"
                                loading={
                                    slideIndex === 0
                                        ? 'eager'
                                        : 'lazy'
                                }
                                className={`hero-slide ${
                                    slideIndex === idx
                                        ? 'hero-slide-active'
                                        : ''
                                }`}
                            />
                        ))}

                        {slides.length === 0 ? (
                            <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--kche-yellow))] via-[hsl(var(--kche-pink))] to-[hsl(var(--kche-cyan))]" />
                        ) : null}
                    </div>

                    {/* Curva orientada como la referencia */}
                    <div
                        className="hero-rainbow"
                        aria-hidden="true"
                    />
                </div>
                
                {slides.length > 1 ? (
                    <div className="absolute z-40 bottom-5 left-[42%] -translate-x-1/2 flex items-center gap-2">
                        {slides.map((_, slideIndex) => (
                            <button
                                type="button"
                                key={slideIndex}
                                aria-label={`Ir al slide ${slideIndex + 1}`}
                                onClick={() => setIdx(slideIndex)}
                                className={`h-2 rounded-full transition-all duration-300 ${
                                    slideIndex === idx
                                        ? 'w-10 bg-[hsl(var(--kche-yellow))]'
                                        : 'w-2 bg-white/75'
                                }`}
                            />
                        ))}
                    </div>
                ) : null}
            </motion.div>

            {/* El wrapper no bloquea el hover de la imagen */}
            <div className="relative z-40 h-full w-full px-4 sm:px-6 lg:px-8 pointer-events-none">
                <div className="py-10 sm:py-12 lg:ml-[62%] lg:flex lg:h-full lg:w-[38%] lg:items-center lg:py-5 lg:pl-10 lg:pr-6 xl:pl-14 pointer-events-auto">
                    <motion.div
                        initial="hidden"
                        animate="show"
                        variants={stagger}
                        className="text-center text-white lg:text-left"
                    >

                        <motion.div
                            variants={fadeUp}
                            className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-4 py-2 text-xs sm:text-sm font-bold backdrop-blur"
                        >
                            <span className="inline-block size-2 rounded-full bg-[hsl(var(--kche-yellow))] animate-pulse" />
                            Nuevo en Pifo, Quito
                        </motion.div>

                        <motion.h1
                            variants={fadeUp}
                            data-testid="home-hero-headline"
                            className="mt-4 font-display font-bold text-[39px] sm:text-[51px] lg:text-[clamp(3rem,3.6vw,4.45rem)] leading-[0.98] tracking-tight text-[hsl(var(--kche-yellow))] drop-shadow-[0_4px_20px_rgba(0,0,0,0.32)]"
                        >
                            {headline}
                        </motion.h1>

                        <motion.p
                            variants={fadeUp}
                            className="mx-auto mt-4 max-w-2xl text-lg sm:text-xl lg:mx-0 lg:text-[1.3rem] font-medium leading-snug text-white/95"
                        >
                            {subheadline}
                        </motion.p>

                        <motion.div
    variants={fadeUp}
    className="mt-5 flex flex-wrap items-center justify-center gap-3 lg:justify-start"
>
    <ReservationButton
        from="hero"
        size="lg"
        tone="yellow"
        testId="home-hero-primary-cta"
    >
        {ctaPrimary}
    </ReservationButton>

    <a
        href={ROUTES.atracciones}
        onClick={onSecondary}
        data-testid="home-hero-secondary-cta"
        className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[hsl(var(--kche-pink))] to-[hsl(var(--kche-purple))] px-7 py-3.5 font-display text-base font-bold text-white shadow-[0_12px_32px_rgba(255,10,120,0.35)] transition-transform duration-300 hover:scale-[1.04] active:scale-[0.98]"
    >
        {ctaSecondary}
        <ArrowRight className="size-5" />
    </a>
</motion.div>

                        <motion.div
                            variants={fadeUp}
                            className="mt-5 inline-flex max-w-full items-center gap-2 rounded-full border border-white/30 bg-white/10 px-5 py-2.5 text-sm text-white/95 backdrop-blur"
                        >
                            <MapPin className="size-4 shrink-0 text-[hsl(var(--kche-yellow))]" />

                            <span className="text-left">
                                {locationLabel}
                            </span>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Hero;